import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { AppFrame } from "@/components/AppFrame";
import { Eyebrow } from "@/components/primitives/Eyebrow";
import { Heading } from "@/components/primitives/Heading";
import { Hairline } from "@/components/primitives/Hairline";
import type { Team } from "@offsite/shared";

/**
 * Beacon Grouping-Stage (`/workshop/grouping`).
 * Phase im Briefing nicht erwähnt — Beacon-Greenfield-Screen für Team-Reveal.
 *
 * Teams werden in einem Hairline-Grid angezeigt, mit Captain-Hervorhebung.
 */
export function GroupingStage() {
  const { state } = useStore();
  const token = state.auth.token;
  const [teams, setTeams] = useState<Team[]>([]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.teams.list(token);
        if (!cancelled) setTeams(res.teams);
      } catch {
        /* swallow */
      }
    };
    load();
    const id = setInterval(load, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token]);

  return (
    <AppFrame skin="presenter" hideHeader hideFooter>
      <section
        style={{
          flex: 1,
          padding: "var(--s-7)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--s-7)",
        }}
      >
        <header
          className="rise-1"
          style={{ display: "flex", flexDirection: "column", gap: "var(--s-3)" }}
        >
          <Eyebrow accent>WORKSHOP · TEAM-ZUTEILUNG</Eyebrow>
          <Heading level="display">Eure Teams.</Heading>
        </header>
        <Hairline skin="presenter" />
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: "var(--s-5)",
          }}
        >
          {teams.map((team, idx) => (
            <li
              key={team.number}
              className={`rise-${Math.min(6, (idx % 6) + 1)}`}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--s-2)",
                padding: "var(--s-5)",
                background: "rgba(255,255,255,0.04)",
              }}
            >
              <span
                className="mono"
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  letterSpacing: "var(--tracking-meta)",
                  textTransform: "uppercase",
                  color: "var(--accent-blue-soft)",
                }}
              >
                T-{String(team.number).padStart(2, "0")}
              </span>
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "var(--t-h3)",
                  fontWeight: 700,
                  letterSpacing: "var(--tracking-h)",
                  color: "var(--svo-weiss, #fff)",
                }}
              >
                {team.name ?? `Team ${team.number}`}
              </span>
            </li>
          ))}
          {teams.length === 0 && (
            <li
              className="mono"
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 14,
                color: "var(--ink-400)",
                textTransform: "uppercase",
                letterSpacing: "var(--tracking-meta)",
                padding: "var(--s-5)",
              }}
            >
              TEAMS WERDEN GLEICH ZUGETEILT
            </li>
          )}
        </ul>
      </section>
    </AppFrame>
  );
}
